import React, { useCallback, useEffect, useState } from 'react';
import { PropTypes as PT } from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import VbbSettings from 'components/VbbSettings/VbbSettings';
import AvSettings from 'components/AvSettings/AvSettings';
import { isChrome } from 'utils/browser-utils';
import classes from './UserSettings.module.scss';
import { hooks } from '@lifesize/clients.sdk';
import { stopWebcam } from 'actions/media';
import Logger from "js-logger";
import { isCallDisconnected } from 'utils/call-utils';
import { useAcquireWebcam } from 'hooks/useAcquireWebcam';
import { vbbManager } from 'utils/vbbManager';
import useVideoDeviceChanged from 'hooks/useVideoDeviceChanged';
import { vbbSettingsSelector } from 'selectors/media';

const UserSettings = (props) => {
  const { hasCamera, hasMicrophone, vbbFeatureEnabled } = props;
  const dispatch = useDispatch();
  const videoDeviceChanged = useVideoDeviceChanged();
  const { localVideoMuted } = hooks.useMedia();
  const { selection: bgSelection, enabled: vbbEnabled } = useSelector(vbbSettingsSelector);
  const mediaSettings = useSelector((state) => state?.app?.mediaSettings);
  const acquireWebcam = useAcquireWebcam();
  const [showVbbSettings, setShowVbbSettings] = useState(false);
  const activeCall = hooks.useCallState();
  const isInCall = !isCallDisconnected(activeCall);

  const handleNewCameraVBB = useCallback(() => {
    vbbManager.handleCameraChange(dispatch, localVideoMuted, vbbEnabled, bgSelection, mediaSettings);
  }, [dispatch, localVideoMuted, vbbEnabled, bgSelection, mediaSettings]);

  useEffect(() => {
    // componentDidMount
    if (!isInCall) {
      acquireWebcam();
    }

    return function componentWillUnmount() {
      Logger.info('UserSettings: Stop Webcam');
      dispatch(stopWebcam());
    };
    // note: an empty dependency array indicates that the effect is only run once (equivalent to componentDidMount() & [componentWillUnmount()])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (videoDeviceChanged) {
      handleNewCameraVBB();
    }
  }, [videoDeviceChanged, handleNewCameraVBB]);

  const onToggleVbbSettings = () => {
    setShowVbbSettings(!showVbbSettings);
  };

  if (vbbFeatureEnabled && showVbbSettings)
    return <VbbSettings onToggleVbbSettings={onToggleVbbSettings} />;

  return (
    <div className={classes.container} id="userSettingsContainer">
      <header>
        <h1>
          <FormattedMessage id="settings.userSettings.avSettings" defaultMessage="Audio/Video Settings" />
        </h1>
        {!hasCamera && (
          <div>
            <div className={classes.missingCameraText}>
              {hasMicrophone ? (
                <FormattedMessage
                  id="settings.userSettings.missingCamera"
                  defaultMessage="We do not have access to your camera. If you proceed with these settings, all of your calls will be audio only."
                />
              ) : (
                <FormattedMessage
                  id="settings.userSettings.missingCameraAndMic"
                  defaultMessage="We do not have access to your camera or microphone."
                />
              )}
            </div>
            {isChrome() && !window.desktopApp && (
              <div className={classes.missingCameraText}>
                <FormattedMessage
                  id="settings.userSettings.chromeInstructions"
                  defaultMessage="For the best experience, go to your Chrome settings to allow access. "
                />
                <a href="https://support.google.com/chrome/answer/2693767" rel="noopener noreferrer" target="_blank">
                  <FormattedMessage id="settings.userSettings.learnMore" defaultMessage="Learn more." />
                </a>
              </div>
            )}
          </div>
        )}
      </header>
      <AvSettings onToggleVbbSettings={onToggleVbbSettings} />
    </div>
  );
}

UserSettings.propTypes = {
  hasCamera: PT.bool,
  hasMicrophone: PT.bool,
  showNotificationSettings: PT.bool,
  vbbFeatureEnabled: PT.bool
};

export default injectIntl(UserSettings);
